import React, { useCallback, useContext, useState } from 'react'
import tokens from 'config/constants/tokens'
import { useTranslation } from 'contexts/Localization'
import { TabMenu, Tab, CardFooter, Button, Text, useModal, AutoRenewIcon } from 'vvs-uikit'
import { Link, Route, useHistory, useLocation } from 'react-router-dom'
import { useWeb3React } from '@web3-react/core'
import Dots from 'components/Loader/Dots'
import { AutoColumn } from 'components/Layout/Column'
import { useUserVvsBoost } from 'state/xVvs/hooks'
import useVvsBoostHandlers from 'views/xVvs/hooks/useVvsBoostHandlers'
import { fetchVvsBoostUserDataAsync } from 'state/xVvs'
import { useAppDispatch } from 'state'
import { useERC20 } from 'hooks/useContract'
import _ from 'lodash'
import { BIG_ZERO } from 'utils/bigNumber'
import xVvsContext from 'views/xVvs/context'
import ConnectWalletButton from 'components/ConnectWalletButton'
import { useVaultsApr } from 'views/xVvs/hooks/useVaultsApr'
import { formatNumber } from 'utils/formatBalance'
import { StyledCard, Body, OuterBody } from '../../styles'
import StyledVvsBarCardHeader from '../StyledVvsBarCardHeader'
import EarningsBody from './EarningsBody'
import VaultInnerCard from './VaultInnerCard'
import LockModal from '../LockModal'

const VaultBody: React.FC = () => {
  const { t } = useTranslation()
  const { account } = useWeb3React()

  const { boostData, userDataLoaded } = useUserVvsBoost()
  const vaultsApr = useVaultsApr()
  const earnMaxApr = vaultsApr.length === 0 ? 0 : Math.max(...vaultsApr.map((x) => Number(x.apr)))

  const renderBody = () => {
    if (!account) {
      return (
        <Text color="textSubtle" textAlign="center">
          {t('Connect to a wallet to view your vaults.')}
        </Text>
      )
    }
    if (!userDataLoaded) {
      return (
        <Text color="textSubtle" textAlign="center">
          <Dots>{t('Loading')}</Dots>
        </Text>
      )
    }
    if (boostData.stakes.filter((x) => x.active).length === 0) {
      return (
        <AutoColumn gap="8px">
          <Text bold textAlign="center">
            {t('No Vault found.')}
          </Text>
          <Text color="textSubtle" textAlign="center">
            {t('Deposit your xVVS now and earn up to ')}
            {formatNumber(earnMaxApr)}% APR
          </Text>
        </AutoColumn>
      )
    }

    const grouped = _.groupBy(
      boostData.stakes
        .map((x, i) => ({
          ...x,
          stakeId: i,
        }))
        .filter((x) => x.active),
      (p) => p.poolId,
    )

    const aggregated = Object.keys(grouped).map((k) =>
      grouped[k]
        .map((x) => ({
          ...x,
          stakeIds: [] as number[],
          withdrawableStakeIds: [] as number[],
        }))
        .reduce(
          (a, b) => {
            const { withdrawableStakeIds, stakeIds } = a
            const isPeriodFulfilled = b.unlockTimestamp <= new Date().getTime() / 1000
            stakeIds.push(b.stakeId)
            if (isPeriodFulfilled) withdrawableStakeIds.push(b.stakeId)
            return {
              ...a,
              amount: a.amount.plus(b.amount),
              totalLocked: a.totalLocked.plus(!isPeriodFulfilled ? b.amount : BIG_ZERO),
              totalUnlocked: a.totalUnlocked.plus(isPeriodFulfilled ? b.amount : BIG_ZERO),
              poolId: b.poolId,
              stakeIds,
              withdrawableStakeIds,
              lastUnlockTimestamp: Math.max(a.lastUnlockTimestamp, b.unlockTimestamp),
            }
          },
          {
            amount: BIG_ZERO,
            totalLocked: BIG_ZERO,
            totalUnlocked: BIG_ZERO,
            poolId: 0,
            stakeTimestamp: 0,
            unlockTimestamp: 0,
            active: false,
            stakeIds: [],
            withdrawableStakeIds: [],
            lastUnlockTimestamp: 0,
          },
        ),
    )

    return aggregated.map((stake) => <VaultInnerCard {...stake} totalDeposited={stake.amount} key={stake.poolId} />)
  }
  return <Body>{renderBody()}</Body>
}

const VaultCard: React.FC = () => {
  const { t } = useTranslation()
  const history = useHistory()
  const location = useLocation()

  const xVvsContract = useERC20(tokens.xvvs.address)

  const { account } = useWeb3React()

  const [requestedApproval, setRequestedApproval] = useState<boolean>()
  const dispatch = useAppDispatch()

  const { setStep, onReset } = useContext(xVvsContext)

  const [openLockModal] = useModal(<LockModal customOnDismiss={onReset} />)

  const handleOpenLockModal = () => {
    setStep(0)
    openLockModal()
  }

  const { boostData } = useUserVvsBoost()
  const { allowance } = boostData

  const { onApprove } = useVvsBoostHandlers()

  let activeIndex
  switch (location.pathname) {
    case '/xvvs/vault':
      activeIndex = 0
      break
    case '/xvvs/vault/earnings':
      activeIndex = 1
      break
    default:
      activeIndex = -1
      break
  }

  const handleApprove = useCallback(async () => {
    try {
      setRequestedApproval(true)
      await onApprove(xVvsContract)
      dispatch(fetchVvsBoostUserDataAsync(account))

      setRequestedApproval(false)
    } catch (e) {
      console.error(e)
    }
  }, [onApprove, dispatch, account, xVvsContract])

  const handleItemClick = (idx: number) => {
    switch (idx) {
      case 0:
        history.push('/xvvs/vault')
        break
      case 1:
        history.push('/xvvs/vault/earnings')
        break
      default:
        history.push('/xvvs/vault')
        break
    }
  }

  const renderContent = useCallback((): JSX.Element => {
    return (
      <>
        <Route exact path="/xvvs/vault" component={VaultBody} />
        <Route exact path="/xvvs/vault/earnings" component={EarningsBody} />
      </>
    )
  }, [])

  return (
    <StyledCard>
      <StyledVvsBarCardHeader earningToken={tokens.xvvs} stakingToken={tokens.vvs} isVault />
      <OuterBody>
        <TabMenu activeIndex={activeIndex} onItemClick={handleItemClick} variant="altered">
          <Tab>{t('My Vault')}</Tab>
          <Tab>{t('My Earnings')}</Tab>
        </TabMenu>
        {renderContent()}
        {activeIndex !== 1 && (
          <CardFooter style={{ textAlign: 'center' }}>
            {!account ? (
              <ConnectWalletButton scale="lg" width="100%" />
            ) : allowance && allowance.isGreaterThan('0') ? (
              <Button id="join-pool-button" scale="lg" as={Link} to="#" width="100%" onClick={handleOpenLockModal}>
                <Text color="white" bold as="h2">
                  {t('Deposit xVVS')}
                </Text>
              </Button>
            ) : (
              <Button
                scale="lg"
                variant="primary"
                width="100%"
                mb="8px"
                onClick={handleApprove}
                isLoading={requestedApproval}
                endIcon={requestedApproval ? <AutoRenewIcon spin color="currentColor" /> : null}
              >
                {t('Enable')}
              </Button>
            )}
          </CardFooter>
        )}
      </OuterBody>
    </StyledCard>
  )
}

export default VaultCard
